import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";
import TryItButton from "../../../TryItButton";
import OutputBox from "../../../OutputBox";

export default function JS_Function_Bind() {
  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">
        🔗 JavaScript <strong style={{ color: "red" }}>Function.bind() </strong>
        Method
      </h1>

      <p className="tutorial-intro">
        The <code className="highlight_code">bind()</code> method creates a new
        function with a permanently bound{" "}
        <code className="highlight_code">this</code> value. It can also preset
        initial arguments.
      </p>

      {/* 🧠 Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde68a)",
          padding: "1rem 2rem",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🧠 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            <code className="highlight_code">bind()</code> returns a new
            function with <code className="highlight_code">this</code>
            permanently set.
          </li>
          <li>You can preset arguments when binding the function.</li>
          <li>
            The new function can be called later without changing the context.
          </li>
          <li>
            Unlike <code className="highlight_code">call()</code> or{" "}
            <code className="highlight_code">apply()</code>, the original
            function is not invoked immediately.
          </li>
        </ul>
      </div>

      {/* 1️⃣ Basic Example */}
      <div className="tutorial-section">
        <h3>
          1️⃣ Basic <code className="highlight_code">bind()</code> Example
        </h3>
        <p>
          Create a new function with{" "}
          <code className="highlight_code">this</code> permanently bound to an
          object.
        </p>

        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`const person = { name: "Alice" };

function greet(greeting) {
  console.log(greeting + ", " + this.name);
}

const greetAlice = greet.bind(person);

greetAlice("Hello");`}
          />
          <TryItButton
            code={`const person={name:"Alice"};function greet(greeting){console.log(greeting+", "+this.name);}const greetAlice=greet.bind(person);greetAlice("Hello");`}
          />
        </div>
        <OutputBox>Output: Hello, Alice</OutputBox>
      </div>

      {/* 2️⃣ Presetting Arguments */}
      <div className="tutorial-section">
        <h3>
          2️⃣ Presetting Arguments with{" "}
          <code className="highlight_code">bind()</code>
        </h3>
        <p>You can bind a function and preset some or all of its arguments.</p>

        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`function multiply(a, b) {
  return a * b;
}

const double = multiply.bind(null, 2); // preset a = 2

console.log(double(5));`}
          />
          <TryItButton
            code={`function multiply(a,b){return a*b;}const double=multiply.bind(null,2);console.log(double(5));`}
          />
        </div>
        <OutputBox>Output: 10</OutputBox>
      </div>

      {/* 3️⃣ Using bind() with Event Listeners */}
      <div className="tutorial-section">
        <h3>3️⃣ bind() with Event Listeners</h3>
        <p>
          Useful for ensuring <code className="highlight_code">this</code>{" "}
          refers to the correct object in callbacks.
        </p>

        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`const button = {
  label: "Click me",
  click: function() {
    console.log("Button: " + this.label);
  }
};

const clickHandler = button.click.bind(button);

clickHandler();`}
          />
          <TryItButton
            code={`const button={label:"Click me",click:function(){console.log("Button: "+this.label);}};const clickHandler=button.click.bind(button);clickHandler();`}
          />
        </div>
        <OutputBox>Output: Button: Click me</OutputBox>
      </div>

      {/* 4️⃣ Comparison with call() and apply() */}
      <div className="tutorial-section">
        <h3>4️⃣ bind() vs call() vs apply()</h3>
        <p>
          <code className="highlight_code">call()</code> and{" "}
          <code className="highlight_code">apply()</code> invoke the function
          immediately, whereas <code className="highlight_code">bind()</code>{" "}
          returns a new function to call later.
        </p>

        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`function sayHi(greeting) {
  console.log(greeting + ", " + this.name);
}

const user = { name: "David" };

sayHi.call(user, "Hello");  // called immediately
sayHi.apply(user, ["Hi"]);  // called immediately

const sayHiUser = sayHi.bind(user); // returns new function
sayHiUser("Hey");`}
          />
          <TryItButton
            code={`function sayHi(greeting){console.log(greeting+", "+this.name);}const user={name:"David"};sayHi.call(user,"Hello");sayHi.apply(user,["Hi"]);const sayHiUser=sayHi.bind(user);sayHiUser("Hey");`}
          />
        </div>
        <OutputBox>
          Output:
          <pre>
            {`Hello, David
Hi, David
Hey, David`}
          </pre>
        </OutputBox>
      </div>

      {/* 🚀 Next Step */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef3c7, #fde68a)",
          padding: "1rem 2rem",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Next Step</h2>
        <p>
          Review <strong>Function.call()</strong> and{" "}
          <strong>Function.apply()</strong> to see how they differ from{" "}
          <code className="highlight_code">bind()</code>.
        </p>
      </div>
    </div>
  );
}
